import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { Layout } from '@/hooks/useMediaQuery';
import { DetailList } from './ui';

export interface DetailItem {
  label: string;
  value: ReactNode;
  className?: string;
  /** Left off the tablet card because its title already says it. */
  notOnCard?: boolean;
}

/** One record of a list, described once for the phone and tablet layouts. */
export interface ResponsiveRecord {
  key: string;
  /** The card's title on a tablet, e.g. the vendor. */
  title: ReactNode;
  /** A small line under the title, e.g. the tracking ID. */
  subtitle?: ReactNode;
  /** The one-line row on a phone; the title when not given. */
  heading?: ReactNode;
  /** Status, shown on the right of the row and the card. */
  badge?: ReactNode;
  details: DetailItem[];
  /** Anything after the details: flags such as "Needs attention". */
  extra?: ReactNode;
  /** Where the record opens; the whole tablet card links there. */
  href?: string;
  /** Opens the record in place (e.g. its edit form) instead of at an href. */
  onOpen?(): void;
  /** A checkbox to pick the record, e.g. for a batch. */
  select?: { checked: boolean; onChange(): void; label: string };
  /** Buttons for the record (Hold, Remove…), under its details. */
  actions?: ReactNode;
}

function SelectBox({ select }: { select: NonNullable<ResponsiveRecord['select']> }) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 shrink-0"
      aria-label={select.label}
      checked={select.checked}
      onChange={select.onChange}
    />
  );
}

/**
 * A list that fits the screen: the page's own table on a desktop, a card per
 * record (two to a row) on a tablet, and on a phone one line per record that
 * opens in place to show the details and a button to the record.
 */
export function ResponsiveRecords({
  layout,
  records,
  table,
  openLabel = 'Open',
}: {
  layout: Layout;
  records: ResponsiveRecord[];
  /** The desktop table, unchanged. */
  table: ReactNode;
  /** The button inside an opened phone row, e.g. "Open invoice". */
  openLabel?: string;
}) {
  if (layout === 'desktop') return <>{table}</>;
  if (layout === 'tablet') return <RecordCards records={records} />;
  return <RecordAccordion records={records} openLabel={openLabel} />;
}

function RecordCards({ records }: { records: ResponsiveRecord[] }) {
  return (
    <div className="grid gap-3 p-4 md:grid-cols-2">
      {records.map((record) => {
        const body = (
          <>
            <div className="mb-1 flex items-start justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2">
                {record.select ? <SelectBox select={record.select} /> : null}
                <span className="min-w-0 truncate font-semibold text-ink-900">{record.title}</span>
              </span>
              {record.badge ? (
                <span className="shrink-0 whitespace-nowrap">{record.badge}</span>
              ) : null}
            </div>
            {record.subtitle ? (
              <p className="mb-3 font-mono text-xs text-ink-500">{record.subtitle}</p>
            ) : (
              <div className="mb-3" />
            )}
            <DetailList items={record.details.filter((item) => !item.notOnCard)} />
            {record.extra ? <div className="mt-3 flex flex-wrap gap-2">{record.extra}</div> : null}
            {record.actions ? (
              <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-ink-100 pt-3">
                {record.actions}
                {record.href ? (
                  <Link className="text-sm font-medium text-brand-700" to={record.href}>
                    Open
                  </Link>
                ) : null}
              </div>
            ) : null}
          </>
        );
        const className =
          'block rounded-xl border border-ink-100 bg-white p-4 shadow-sm transition-colors';
        // A card with buttons of its own cannot also be one big link.
        const whole = !record.actions && !record.select;
        return record.href && whole ? (
          <Link key={record.key} to={record.href} className={`${className} hover:border-brand-300`}>
            {body}
          </Link>
        ) : record.onOpen && whole ? (
          <div
            key={record.key}
            role="button"
            tabIndex={0}
            className={`${className} cursor-pointer text-left hover:border-brand-300`}
            onClick={record.onOpen}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                record.onOpen?.();
              }
            }}
          >
            {body}
          </div>
        ) : (
          <div key={record.key} className={className}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/** One open at a time, so the list stays short. */
function RecordAccordion({
  records,
  openLabel,
}: {
  records: ResponsiveRecord[];
  openLabel: string;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <ul className="divide-y divide-ink-100">
      {records.map((record) => {
        const open = openKey === record.key;
        const panelId = `record-${record.key}`;
        return (
          <li key={record.key} className={open ? 'bg-brand-50/40' : undefined}>
            <div className="flex items-center">
              {record.select ? (
                <span className="flex items-center pl-4">
                  <SelectBox select={record.select} />
                </span>
              ) : null}
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3.5 text-left"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpenKey(open ? null : record.key)}
              >
                <span className="min-w-0 truncate text-sm font-medium text-ink-900">
                  {record.heading ?? record.title}
                </span>
                <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                  {record.badge}
                  <ChevronDown
                    className={`h-4 w-4 text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </button>
            </div>
            {open ? (
              <div id={panelId} className="space-y-3 px-4 pb-4">
                <DetailList items={record.details} />
                <div className="flex flex-wrap items-center gap-3">
                  {record.href ? (
                    <Link className="btn-primary" to={record.href}>
                      {openLabel}
                    </Link>
                  ) : record.onOpen ? (
                    <button type="button" className="btn-primary" onClick={record.onOpen}>
                      {openLabel}
                    </button>
                  ) : null}
                  {record.extra}
                  {record.actions}
                </div>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
